
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  X,
  Trash2,
  Tag,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Truck
} from 'lucide-react';
import {
  fetchShippingSettings,
  getCachedShippingSettings,
  calculateShipping
} from '../utils/shippingConfig';
import {
  getGuestCart,
  updateGuestCartQty,
  removeGuestCartItem
} from '../utils/cartHelper';

const API_BASE = "https://api.vanyacollectionsrajahmundry.com";

const CartPage = ({ isOpen, onClose }) => {
  const [cartItems, setCartItems] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [shippingSettings, setShippingSettings] = useState(getCachedShippingSettings);

  const [couponPopup, setCouponPopup] = useState({
    show: false,
    title: "",
    message: "",
    success: false,
  });

  const navigate = useNavigate();

  // ==========================================================
  // GET USER ID
  // ==========================================================

  const storedUser = localStorage.getItem("user");

  let userId = null;

  try {
    userId = storedUser ? JSON.parse(storedUser).id : null;
  } catch (error) {
    console.error("Invalid user data:", error);
  }

  // ==========================================================
  // COUPON POPUP
  // ==========================================================

  const showCouponPopup = (title, message, success = true) => {
    setCouponPopup({
      show: true,
      title,
      message,
      success,
    });

    setTimeout(() => {
      setCouponPopup({
        show: false,
        title: "",
        message: "",
        success: false,
      });
    }, 2000);
  };

  // ==========================================================
  // FETCH CART
  // ==========================================================

  const fetchCart = async () => {
    if (!userId) {
      const guestItems = getGuestCart();
      const normalizedGuest = guestItems.map(item => {
        const itemImg =
          item.img_url ||
          item.image ||
          item.imageUrl ||
          item.img ||
          item.mainImage ||
          item.variant?.mainImage ||
          item.variant?.img_url ||
          (Array.isArray(item.thumbnails) && item.thumbnails[0]) ||
          "";

        return {
          ...item,
          price: Number(item.variant?.price ?? item.price ?? 0),
          oldPrice: Number(item.variant?.oldPrice ?? item.oldPrice ?? 0),
          quantity: Number(item.quantity || 1),
          subtotal: Number(item.variant?.price ?? item.price ?? 0) * Number(item.quantity || 1),
          img_url: itemImg,
          image: itemImg,
          imageUrl: itemImg,
          img: itemImg,
        };
      });
      setCartItems(normalizedGuest);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/cart/${userId}`);

      if (!res.ok) {
        throw new Error("Failed to fetch cart");
      }

      const data = await res.json();

      console.log("CART API RESPONSE:", data);

      const items = Array.isArray(data.items)
        ? data.items.map(item => {

            // ==================================================
            // VARIANT DATA
            // ==================================================

            const variant =
              item.variant &&
              typeof item.variant === "object"
                ? item.variant
                : {};

            // ==================================================
            // SELECT VARIANT IMAGE
            // Priority:
            // variant.mainImage
            // item.img_url
            // variant.thumbnails[0]
            // ==================================================

            const variantImage =
              variant.mainImage ||
              item.img_url ||
              (
                Array.isArray(variant.thumbnails) &&
                variant.thumbnails.length > 0
                  ? variant.thumbnails[0]
                  : null
              );

            // ==================================================
            // SELECT VARIANT PRICE
            // ==================================================

            const variantPrice =
              variant.price !== null &&
              variant.price !== undefined
                ? Number(variant.price)
                : Number(item.price || 0);

            // ==================================================
            // OLD PRICE
            // ==================================================

            const variantOldPrice =
              variant.oldPrice !== null &&
              variant.oldPrice !== undefined
                ? Number(variant.oldPrice)
                : (
                    item.oldPrice !== null &&
                    item.oldPrice !== undefined
                      ? Number(item.oldPrice)
                      : null
                  );

            // ==================================================
            // COLOUR
            // ==================================================

            const variantColour =
              variant.colour ||
              item.colour ||
              null;

            // ==================================================
            // SIZE
            // ==================================================

            const variantSize =
              variant.size ||
              item.size ||
              null;

            // ==================================================
            // DISCOUNT
            // ==================================================

            const variantDiscount =
              variant.discount !== null &&
              variant.discount !== undefined
                ? Number(variant.discount)
                : Number(item.discount || 0);

            // ==================================================
            // STOCK
            // ==================================================

            const variantStock =
              variant.stock !== null &&
              variant.stock !== undefined
                ? Number(variant.stock)
                : (item.stock !== null && item.stock !== undefined
                    ? Number(item.stock)
                    : null);

            // ==================================================
            // RETURN NORMALIZED CART ITEM
            // ==================================================

            return {
              ...item,

              variant,

              img_url: variantImage,

              price: variantPrice,

              oldPrice: variantOldPrice,

              colour: variantColour,

              size: variantSize,

              discount: variantDiscount,

              stock: variantStock,

              quantity: Number(item.quantity || 1),

              subtotal:
                variantPrice * Number(item.quantity || 1),
            };
          })
        : [];

      setCartItems(items);
      localStorage.setItem("vanya_user_cart_cache", JSON.stringify(items));
      window.dispatchEvent(new Event("vanya_cart_change"));

    } catch (err) {
      console.error("Failed to fetch cart:", err);
      setCartItems([]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCart();
    }
  }, [isOpen, userId]);

  // ==========================================================
  // FETCH COUPONS
  // ==========================================================

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/cart/coupons/all`
        );

        const data = await res.json();

        if (data.success) {
          setCoupons(data.coupons || []);
        }

      } catch (err) {
        console.error(
          "Failed to fetch coupons:",
          err
        );
      }
    };

    if (isOpen) {
      fetchCoupons();

      // Fetch dynamic shipping rules
      const loadShipping = async () => {
        try {
          const s = await fetchShippingSettings();
          if (s) setShippingSettings(s);
        } catch (e) {
          console.warn("Shipping settings fetch failed:", e);
        }
      };
      loadShipping();
    }

    const handleStorageChange = () => {
      setShippingSettings(getCachedShippingSettings());
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [isOpen]);

  // ==========================================================
  // UPDATE QUANTITY
  // ==========================================================

  const handleUpdateQty = async (cartId, change) => {
    const item = cartItems.find(
      item => item.id === cartId
    );

    if (!item) return;

    const currentQty = Number(item.quantity || 1);

    const newQty = Math.max(
      1,
      currentQty + change
    );

    // ========================================================
    // STOCK CHECK
    // ========================================================

    const availableStock =
      item.variant?.stock !== undefined && item.variant?.stock !== null
        ? Number(item.variant.stock)
        : (item.stock !== undefined && item.stock !== null ? Number(item.stock) : null);

    if (
      availableStock !== null &&
      newQty > availableStock
    ) {
      showCouponPopup(
        "Stock Limit",
        `Only ${availableStock} item(s) available.`,
        false
      );

      return;
    }

    // ========================================================
    // UPDATE UI
    // ========================================================

    setCartItems(prev => {
      const updatedList = prev.map(cartItem => {

        if (cartItem.id !== cartId) {
          return cartItem;
        }

        const price = Number(
          cartItem.variant?.price ??
          cartItem.price ??
          0
        );

        return {
          ...cartItem,

          quantity: newQty,

          subtotal:
            price * newQty,
        };
      });
      if (userId) {
        localStorage.setItem("vanya_user_cart_cache", JSON.stringify(updatedList));
        window.dispatchEvent(new Event("vanya_cart_change"));
      }
      return updatedList;
    });

    // ========================================================
    // UPDATE BACKEND OR GUEST CART
    // ========================================================

    if (!userId) {
      updateGuestCartQty(cartId, newQty);
      return;
    }

    try {

      // First attempt PUT /cart/update/:cart_id (standard REST & live server route)
      let res = await fetch(
        `${API_BASE}/cart/update/${cartId}`,
        {
          method: "PUT",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({

            // User
            user_id: userId,

            // cart item ID
            cart_id: cartId,

            // Product ID
            product_id: item.product_id,

            // Selected variant information
            colour:
              item.variant?.colour ??
              item.colour ??
              null,

            size:
              item.variant?.size ??
              item.size ??
              null,

            // Quantity
            quantity: newQty,
          }),
        }
      );

      // Fallback in case endpoint without path parameter is expected
      if (res.status === 404) {
        res = await fetch(
          `${API_BASE}/cart/update`,
          {
            method: "PUT",

            headers: {
              "Content-Type": "application/json",
            },

            body: JSON.stringify({
              user_id: userId,
              cart_id: cartId,
              product_id: item.product_id,
              colour:
                item.variant?.colour ??
                item.colour ??
                null,
              size:
                item.variant?.size ??
                item.size ??
                null,
              quantity: newQty,
            }),
          }
        );
      }

      if (!res.ok) {
        throw new Error(
          "Failed to update quantity"
        );
      }

    } catch (err) {

      console.error(
        "Quantity update failed:",
        err
      );

      // Reload actual cart from backend
      fetchCart();
    }
  };

  // ==========================================================
  // REMOVE ITEM
  // ==========================================================

  const handleRemove = async (cartId) => {

    try {

      // Remove immediately from UI
      setCartItems(prev => {
        const updatedList = prev.filter(
          item => item.id !== cartId
        );
        if (userId) {
          localStorage.setItem("vanya_user_cart_cache", JSON.stringify(updatedList));
          window.dispatchEvent(new Event("vanya_cart_change"));
        }
        return updatedList;
      });

      if (!userId) {
        removeGuestCartItem(cartId);
        return;
      }

      const res = await fetch(
        `${API_BASE}/cart/delete/${cartId}`,
        {
          method: "DELETE",
        }
      );

      if (!res.ok) {
        throw new Error(
          "Failed to remove item"
        );
      }

    } catch (err) {

      console.error(err);

      alert(
        "Could not remove item from cart. Try again."
      );

      fetchCart();
    }
  };

  // ==========================================================
  // APPLY COUPON
  // ==========================================================

  const processApplyCoupon = (codeToApply) => {

    const coupon = coupons.find(
      c =>
        c.code
          ?.trim()
          .toLowerCase() ===
        codeToApply
          ?.trim()
          .toLowerCase()
    );

    if (!coupon) {
      showCouponPopup(
        "Invalid Coupon",
        "This coupon code does not exist or has been removed.",
        false
      );
      return;
    }

    // Check if disabled by Admin
    if (coupon.is_active === false) {
      showCouponPopup(
        "Coupon Disabled",
        `Coupon "${coupon.code}" is currently disabled and cannot be applied.`,
        false
      );
      return;
    }

    // Check if expired
    if (coupon.expiry_date) {
      const expDate = new Date(coupon.expiry_date);
      expDate.setHours(23, 59, 59, 999);
      if (expDate < new Date()) {
        const expFormatted = new Date(coupon.expiry_date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
        showCouponPopup(
          "Coupon Expired",
          `Coupon "${coupon.code}" expired on ${expFormatted}.`,
          false
        );
        return;
      }
    }

    // ========================================================
    // CHECK ELIGIBLE ITEMS
    // ========================================================

    const eligibleItems =
      cartItems.filter(item => {

        if (coupon.apply_type === "category") {

          return (
            item.category
              ?.trim()
              .toLowerCase() ===
            coupon.category_name
              ?.trim()
              .toLowerCase()
          );
        }

        if (coupon.apply_type === "product") {

          return (
            Number(item.product_id) ===
            Number(coupon.product_id)
          );
        }

        return true;
      });

    if (eligibleItems.length === 0) {

      showCouponPopup(
        "Coupon Not Applicable",
        "This coupon doesn't apply to any items in your cart.",
        false
      );

      return;
    }

    // ========================================================
    // ELIGIBLE SUBTOTAL
    // ========================================================

    const eligibleSubtotal =
      eligibleItems.reduce(
        (sum, item) => {

          const price =
            Number(
              item.variant?.price ??
              item.price ??
              0
            );

          return (
            sum +
            price *
              Number(item.quantity || 1)
          );
        },
        0
      );

    // ========================================================
    // MINIMUM PURCHASE
    // ========================================================

    if (
      eligibleSubtotal <
      Number(coupon.min_amount || 0)
    ) {

      showCouponPopup(
        "Minimum Purchase Required",
        `Minimum purchase of ₹${coupon.min_amount} is required.`,
        false
      );

      return;
    }

    // ========================================================
    // APPLY
    // ========================================================

    setAppliedCoupon(coupon);

    setCouponInput(coupon.code);

    showCouponPopup(
      "Coupon Applied",
      `${coupon.code} applied successfully!`,
      true
    );
  };

  const handleApplyCoupon = () => {
    processApplyCoupon(couponInput);
  };

  // ==========================================================
  // SUBTOTAL
  // ==========================================================

  const subtotal = cartItems.reduce(
    (acc, item) => {

      const price =
        Number(
          item.variant?.price ??
          item.price ??
          0
        );

      const quantity =
        Number(item.quantity || 1);

      return acc + price * quantity;

    },
    0
  );

  // ==========================================================
  // DISCOUNT
  // ==========================================================

  const discountAmount = appliedCoupon
    ? appliedCoupon.discount_type === "percentage"
      ? (
          subtotal *
          parseFloat(
            appliedCoupon.discount_value
          )
        ) / 100
      : parseFloat(
          appliedCoupon.discount_value
        )
    : 0;

  // ==========================================================
  // SHIPPING & FINAL TOTAL
  // ==========================================================

  const totalPieces = cartItems.reduce(
    (acc, item) => acc + (Number(item.quantity) || Number(item.qty) || 1),
    0
  );

  const shippingInfo = calculateShipping(subtotal, shippingSettings, "", totalPieces);
  const shippingFee = shippingInfo.shippingCost;
  const isFreeShipping = shippingInfo.isFree;

  const discountedTotal =
    Math.max(
      0,
      subtotal - discountAmount
    );

  // Cart total does not include shipping; shipping charges vary by state and are calculated at checkout
  const finalCartTotal = discountedTotal;

  const hasOutOfStockItems = cartItems.some(item => {
    const vStock = item.variant?.stock !== null && item.variant?.stock !== undefined
      ? Number(item.variant.stock)
      : (item.stock !== null && item.stock !== undefined ? Number(item.stock) : null);
    if (vStock === null) return false;
    return vStock <= 0 || Number(item.quantity || 1) > vStock;
  });

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <>
      <style>{`

        .cart-drawer {
          position: fixed;
          top: 0;
          right: -480px;
          width: 450px;
          max-width: 100%;
          height: 100%;
          background: #ffffff;
          box-shadow:
            -15px 0 40px rgba(74, 35, 90, 0.15);
          z-index: 2001;
          transition:
            right 0.4s
            cubic-bezier(0.16, 1, 0.3, 1);
          display: flex;
          flex-direction: column;
          font-family: 'Inter', sans-serif;
        }

        .cart-drawer.open {
          right: 0;
        }

        .cart-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(74, 35, 90, 0.5);
          backdrop-filter: blur(6px);
          z-index: 2000;
          animation: fadeIn 0.3s ease;
        }

        .cart-header {
          padding: 24px;
          background: #5b2c6f;
          color: #f39c12;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom:
            1px solid rgba(243, 156, 18, 0.25);
        }

        .header-title {
          font-family: 'Playfair Display', serif;
          font-weight: 700;
          font-size: 20px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .item-count {
          color: #5b2c6f;
          font-weight: 700;
          font-size: 13px;
          background: #f39c12;
          padding: 2px 10px;
          border-radius: 20px;
        }

        .close-btn {
          background:
            rgba(255, 255, 255, 0.1);
          border: none;
          color: #f39c12;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background:
            rgba(243, 156, 18, 0.2);
          transform: scale(1.05);
        }

        .cart-body {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          background: #f9f8fc;
        }

        .cart-item {
          display: flex;
          gap: 16px;
          padding: 16px;
          border-radius: 16px;
          margin-bottom: 16px;
          background: #ffffff;
          box-shadow:
            0 4px 20px rgba(74, 35, 90, 0.05);
          border:
            1px solid rgba(243, 156, 18, 0.2);
          align-items: flex-start;
          transition:
            transform 0.2s,
            box-shadow 0.2s;
        }

        .cart-item:hover {
          box-shadow:
            0 6px 24px rgba(74, 35, 90, 0.1);
          transform: translateY(-2px);
        }

        .item-img img {
          width: 75px;
          height: 95px;
          object-fit: cover;
          border-radius: 10px;
          box-shadow:
            0 4px 10px rgba(0, 0, 0, 0.08);
        }

        .item-details {
          flex: 1;
          min-width: 0;
        }

        .item-cat {
          font-size: 10px;
          color: #d68910;
          margin: 0;
          letter-spacing: 1.5px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .item-name {
          font-size: 15px;
          margin: 4px 0 8px 0;
          color: #5b2c6f;
          font-weight: 700;
          font-family: 'Playfair Display', serif;
          line-height: 1.3;
        }

        /* =====================================================
           VARIANT DETAILS
        ===================================================== */

        .variant-details {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 8px;
        }

        .variant-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 6px;
          background: #f8f4fa;
          border: 1px solid #eadff0;
          color: #5b2c6f;
          font-size: 10px;
          font-weight: 600;
        }

        .colour-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 1px solid #ddd;
          display: inline-block;
        }

        .price-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .item-price {
          font-weight: 700;
          color: #5b2c6f;
          font-size: 16px;
        }

        .old-price {
          color: #94a3b8;
          text-decoration: line-through;
          font-size: 12px;
        }

        .discount-badge {
          background: #ecfdf5;
          color: #059669;
          padding: 3px 6px;
          border-radius: 5px;
          font-size: 10px;
          font-weight: 700;
        }

        .stock-text {
          margin-top: 5px;
          font-size: 10px;
          color: #64748b;
        }

        .item-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 12px;
        }

        .qty-selector {
          display: flex;
          align-items: center;
          border:
            1.5px solid #e2e8f0;
          border-radius: 30px;
          background: #fff;
          overflow: hidden;
        }

        .qty-selector button {
          border: none;
          background: #f8fafc;
          padding: 6px 12px;
          cursor: pointer;
          color: #5b2c6f;
          font-weight: bold;
          font-size: 14px;
          transition: background 0.2s;
        }

        .qty-selector button:hover {
          background: #e2e8f0;
        }

        .qty-selector span {
          padding: 0 8px;
          font-size: 13px;
          font-weight: 700;
          min-width: 20px;
          text-align: center;
          color: #5b2c6f;
        }

        .remove-btn {
          background: #fff5f5;
          border:
            1px solid #fed7d7;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          color: #e53e3e;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .remove-btn:hover {
          background: #ffe3e3;
          transform: scale(1.05);
        }

        .footer-coupons-block {
          background: #fdfaf6;
          border: 1.5px dashed #f39c12;
          border-radius: 12px;
          padding: 10px 12px;
          margin-bottom: 12px;
        }

        .footer-coupons-header {
          font-size: 11px;
          font-weight: 700;
          color: #5b2c6f;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
        }

        .footer-coupon-chips {
          display: flex;
          overflow-x: auto;
          gap: 8px;
          padding-bottom: 4px;
          scrollbar-width: thin;
        }

        .footer-coupon-chip {
          background: #fff;
          border: 1.5px solid #f9e79f;
          border-radius: 8px;
          padding: 6px 10px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .footer-coupon-chip:hover {
          border-color: #f39c12;
          background: #fef9e7;
          transform: translateY(-1px);
        }

        .footer-coupon-chip.applied {
          background: #ecfdf5;
          border-color: #10b981;
        }

        .chip-code {
          font-weight: 700;
          color: #5b2c6f;
          font-size: 11px;
          display: flex;
          align-items: center;
          gap: 3px;
        }

        .chip-desc {
          color: #b7950b;
          font-size: 11px;
          font-weight: 700;
        }

        .chip-tap-badge {
          font-size: 9px;
          color: #5b2c6f;
          background: #f4ecf7;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 600;
        }

        .chip-applied-badge {
          font-size: 9px;
          color: #047857;
          background: #d1fae5;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 700;
        }

        .cart-footer {
          padding: 16px 20px;
          border-top:
            1px solid #eae6df;
          background: #ffffff;
          box-shadow:
            0 -10px 30px rgba(74, 35, 90, 0.05);
        }

        .coupon-section {
          display: flex;
          gap: 10px;
          margin-bottom: 18px;
        }

        .coupon-section input {
          flex: 1;
          padding: 12px 18px;
          border:
            1.5px solid #dcd6cd;
          border-radius: 30px;
          background: #f9f8fc;
          font-family: inherit;
          font-size: 13px;
          outline: none;
          transition: all 0.2s;
        }

        .coupon-section input:focus {
          border-color: #5b2c6f;
          background: #fff;
          box-shadow:
            0 0 0 3px
            rgba(91, 44, 111, 0.1);
        }

        .apply-btn {
          background: #5b2c6f;
          color: #f39c12;
          border: none;
          padding: 0 22px;
          border-radius: 30px;
          cursor: pointer;
          font-weight: 700;
          font-size: 13px;
          transition: all 0.2s;
        }

        .apply-btn:hover {
          background: #6c3483;
          transform: translateY(-1px);
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .summary-label {
          font-size: 14px;
          color: #64748b;
          font-weight: 500;
        }

        .summary-value {
          font-size: 14px;
          color: #1e293b;
          font-weight: 600;
        }

        .total-row {
          border-top:
            1.5px solid #eae6df;
          padding-top: 12px;
          margin-top: 8px;
        }

        .total-amount {
          font-weight: 800;
          color: #5b2c6f;
          font-size: 22px;
          font-family:
            'Playfair Display', serif;
        }

        .shipping-note {
          font-size: 11px;
          color: #94a3b8;
          margin-bottom: 18px;
          text-align: left;
        }

        .checkout-btn {
          width: 100%;
          background:
            linear-gradient(
              135deg,
              #5b2c6f 0%,
              #7d3c98 100%
            );
          color: #f39c12;
          padding: 16px;
          border: none;
          border-radius: 35px;
          font-weight: 700;
          cursor: pointer;
          margin-bottom: 10px;
          font-size: 15px;
          letter-spacing: 0.5px;
          box-shadow:
            0 6px 20px
            rgba(91, 44, 111, 0.25);
          transition:
            all 0.2s
            cubic-bezier(0.16, 1, 0.3, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .checkout-btn:hover {
          opacity: 0.95;
          transform: translateY(-2px);
          box-shadow:
            0 8px 25px
            rgba(91, 44, 111, 0.35);
        }

        .continue-btn {
          width: 100%;
          background: transparent;
          border:
            1.5px solid #5b2c6f;
          color: #5b2c6f;
          padding: 12px;
          border-radius: 35px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 700;
          transition: all 0.2s;
        }

        .continue-btn:hover {
          background:
            rgba(91, 44, 111, 0.05);
        }

        .coupon-popup-overlay {
          position: fixed;
          inset: 0;
          background:
            rgba(74, 35, 90, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          animation: fadeIn .25s ease;
          padding: 20px;
        }

        .coupon-popup {
          width: 100%;
          max-width: 340px;
          background: #fff;
          border-radius: 24px;
          padding: 30px 24px;
          text-align: center;
          box-shadow:
            0 25px 50px
            rgba(74, 35, 90, 0.25);
          animation:
            popupScale .3s
            cubic-bezier(0.16, 1, 0.3, 1);
          border:
            1px solid
            rgba(243, 156, 18, 0.3);
        }

        .coupon-popup-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .coupon-popup-icon.success {
          background: #ecfdf5;
          color: #059669;
        }

        .coupon-popup-icon.error {
          background: #fef2f2;
          color: #dc2626;
        }

        .coupon-popup h2 {
          margin: 0 0 8px;
          color: #5b2c6f;
          font-size: 20px;
          font-family:
            'Playfair Display', serif;
        }

        .coupon-popup p {
          color: #64748b;
          font-size: 14px;
          line-height: 1.5;
          margin: 0;
        }

        .coupon-popup button {
          margin-top: 24px;
          width: 100%;
          border: none;
          background: #5b2c6f;
          color: #f39c12;
          padding: 14px;
          border-radius: 30px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 700;
          transition: opacity 0.2s;
        }

        .coupon-popup button:hover {
          opacity: 0.9;
        }

        @keyframes popupScale {
          from {
            transform: scale(.85);
            opacity: 0;
          }

          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }

          to {
            opacity: 1;
          }
        }

        @media (max-width: 480px) {

          .cart-drawer {
            width: 100%;
            right: -100%;
          }

          .cart-drawer.open {
            right: 0;
          }

          .cart-header,
          .cart-footer {
            padding: 18px;
          }

          .cart-body {
            padding: 16px;
          }

          .cart-item {
            gap: 12px;
            padding: 12px;
          }

          .item-img img {
            width: 65px;
            height: 85px;
          }
        }

      `}</style>

      {/* ======================================================
          OVERLAY
      ====================================================== */}

      {isOpen && (
        <div
          className="cart-overlay"
          onClick={onClose}
        />
      )}

      {/* ======================================================
          CART DRAWER
      ====================================================== */}

      <div
        className={`cart-drawer ${
          isOpen ? "open" : ""
        }`}
      >

        {/* ====================================================
            HEADER
        ==================================================== */}

        <div className="cart-header">

          <div className="header-title">

            <ShoppingBag
              size={20}
              color="#f39c12"
            />

            Your Bag

            <span className="item-count">
              {cartItems.length}
            </span>

          </div>

          <button
            className="close-btn"
            onClick={onClose}
          >
            <X size={18} />
          </button>

        </div>

        {/* ====================================================
            BODY
        ==================================================== */}

        <div className="cart-body">

          {cartItems.length === 0 ? (

            <div
              style={{
                textAlign: "center",
                marginTop: "120px"
              }}
            >

              <div
                style={{
                  display: "inline-flex",
                  padding: "24px",
                  background: "#f4ecf7",
                  borderRadius: "50%",
                  marginBottom: "16px"
                }}
              >

                <ShoppingBag
                  size={40}
                  color="#7d3c98"
                  style={{ opacity: 0.6 }}
                />

              </div>

              <p
                style={{
                  color: "#5b2c6f",
                  fontSize: "18px",
                  fontWeight: 700,
                  fontFamily:
                    "'Playfair Display', serif",
                  margin: "0 0 6px 0"
                }}
              >
                Your bag is empty
              </p>

              <p
                style={{
                  color: "#64748b",
                  fontSize: "13px",
                  margin: 0
                }}
              >
                Explore our exquisite collection
                of sarees and add your favorites.
              </p>

            </div>

          ) : (

            cartItems.map(item => {

              // ==================================================
              // ALWAYS USE SELECTED VARIANT FIRST
              // ==================================================

              const variant =
                item.variant &&
                typeof item.variant === "object"
                  ? item.variant
                  : {};

              const image =
                variant.mainImage ||
                variant.main_image ||
                variant.img_url ||
                variant.image ||
                item.img_url ||
                item.image ||
                item.imageUrl ||
                item.img ||
                item.mainImage ||
                (Array.isArray(variant.thumbnails) && variant.thumbnails.length
                  ? variant.thumbnails[0]
                  : "") ||
                (Array.isArray(item.thumbnails) && item.thumbnails.length
                  ? item.thumbnails[0]
                  : "");

              const price =
                Number(
                  variant.price ??
                  item.price ??
                  0
                );

              const oldPrice =
                variant.oldPrice !== null &&
                variant.oldPrice !== undefined
                  ? Number(
                      variant.oldPrice
                    )
                  : (
                      item.oldPrice !== null &&
                      item.oldPrice !== undefined
                        ? Number(
                            item.oldPrice
                          )
                        : null
                    );

              const colour =
                variant.colour ||
                item.colour ||
                null;

              const size =
                variant.size ||
                item.size ||
                null;

              const discount =
                variant.discount !== null &&
                variant.discount !== undefined
                  ? Number(
                      variant.discount
                    )
                  : Number(
                      item.discount || 0
                    );

              const stock =
                variant.stock !== null &&
                variant.stock !== undefined
                  ? Number(
                      variant.stock
                    )
                  : Number(
                      item.stock || 0
                    );

              const quantity =
                Number(item.quantity || 1);

              const itemTotal =
                price * quantity;

              return (

                <div
                  key={item.id}
                  className="cart-item"
                >

                  {/* ==================================================
                      SELECTED VARIANT IMAGE
                  ================================================== */}

                  <div className="item-img">
                    {image ? (
                      <img
                        src={image}
                        alt={
                          `${item.name}${
                            colour
                              ? ` - ${colour}`
                              : ""
                          }`
                        }
                        onError={(e) => {
                          e.currentTarget.style.display =
                            "none";
                          if (e.currentTarget.nextSibling) {
                            e.currentTarget.nextSibling.style.display = "flex";
                          }
                        }}
                      />
                    ) : null}
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        minHeight: 80,
                        display: image ? "none" : "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f7f2fa",
                        borderRadius: 8,
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#633878",
                      }}
                    >
                      VANYA
                    </div>
                  </div>

                  <div className="item-details">

                    {/* CATEGORY */}

                    <p className="item-cat">
                      {item.category?.toUpperCase() ||
                        "SILK SAREES"}
                    </p>

                    {/* PRODUCT NAME */}

                    <h4 className="item-name">
                      {item.name}
                    </h4>

                    {/* ==================================================
                        VARIANT DETAILS
                    ================================================== */}

                    {(colour || size) && (

                      <div className="variant-details">

                        {colour && (

                          <span className="variant-badge">

                            <span
                              className="colour-dot"
                              style={{
                                backgroundColor:
                                  colour.toLowerCase()
                              }}
                            />

                            Colour: {colour}

                          </span>

                        )}

                        {size && (

                          <span className="variant-badge">
                            Size: {size}
                          </span>

                        )}

                      </div>

                    )}

                    {/* ==================================================
                        PRICE
                    ================================================== */}

                    <div className="price-wrapper">

                      <span className="item-price">
                        ₹{price.toLocaleString()}
                      </span>

                      {oldPrice &&
                        oldPrice > price && (

                          <span className="old-price">
                            ₹{oldPrice.toLocaleString()}
                          </span>

                        )}

                      {discount > 0 && (

                        <span className="discount-badge">
                          {discount}% OFF
                        </span>

                      )}

                    </div>

                    {/* ==================================================
                        STOCK
                    ================================================== */}

                    {stock !== null && stock <= 0 ? (
                      <div
                        style={{
                          color: "#dc2626",
                          background: "#fef2f2",
                          border: "1px solid #fecaca",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "700",
                          marginTop: "6px",
                          display: "inline-block",
                        }}
                      >
                        ✕ Out of Stock - Please remove to proceed
                      </div>
                    ) : stock !== null && quantity > stock ? (
                      <div
                        style={{
                          color: "#d97706",
                          background: "#fffbeb",
                          border: "1px solid #fde68a",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "700",
                          marginTop: "6px",
                          display: "inline-block",
                        }}
                      >
                        ⚠️ Only {stock} available (Reduce quantity)
                      </div>
                    ) : stock !== null ? (
                      <div className="stock-text">
                        {stock <= 5
                          ? `Only ${stock} left`
                          : `${stock} available`}
                      </div>
                    ) : null}

                    {/* ==================================================
                        ITEM TOTAL
                    ================================================== */}

                    <div
                      style={{
                        marginTop: "5px",
                        fontSize: "13px",
                        color: "#5b2c6f",
                        fontWeight: 700
                      }}
                    >
                      Total: ₹
                      {itemTotal.toLocaleString()}
                    </div>

                    {/* ==================================================
                        ACTIONS
                    ================================================== */}

                    <div className="item-actions">

                      <div className="qty-selector">

                        <button
                          onClick={() =>
                            handleUpdateQty(
                              item.id,
                              -1
                            )
                          }
                        >
                          −
                        </button>

                        <span>
                          {quantity}
                        </span>

                        <button
                          onClick={() =>
                            handleUpdateQty(
                              item.id,
                              1
                            )
                          }
                        >
                          +
                        </button>

                      </div>

                      <button
                        className="remove-btn"
                        onClick={() =>
                          handleRemove(item.id)
                        }
                        title="Remove item"
                      >
                        <Trash2 size={14} />
                      </button>

                    </div>

                  </div>

                </div>

              );
            })

          )}

        </div>

        {/* ======================================================
            FOOTER
        ====================================================== */}

        <div className="cart-footer">

          {cartItems.length > 0 && (
            <>
              {/* AVAILABLE OFFERS (ALWAYS VISIBLE IN FOOTER) */}
              {coupons.filter(c => {
                const isExpired = c.expiry_date && new Date(c.expiry_date).setHours(23, 59, 59, 999) < new Date();
                return c.is_active !== false && !isExpired;
              }).length > 0 && (
                <div className="footer-coupons-block">
                  <div className="footer-coupons-header">
                    <Tag size={12} color="#f39c12" />
                    <span>Available Offers:</span>
                  </div>
                  <div className="footer-coupon-chips">
                    {coupons
                      .filter(c => {
                        const isExpired = c.expiry_date && new Date(c.expiry_date).setHours(23, 59, 59, 999) < new Date();
                        return c.is_active !== false && !isExpired;
                      })
                      .map(c => {
                        const isApplied = appliedCoupon?.code?.trim().toLowerCase() === c.code?.trim().toLowerCase();
                        return (
                          <div
                            key={c.id}
                            className={`footer-coupon-chip ${isApplied ? "applied" : ""}`}
                            onClick={() => processApplyCoupon(c.code)}
                            title={isApplied ? "Coupon applied" : `Click to apply ${c.code}`}
                          >
                            <span className="chip-code">
                              <Tag size={10} />
                              {c.code}
                            </span>
                            <span className="chip-desc">
                              {c.discount_type === "percentage"
                                ? `${c.discount_value}% OFF`
                                : `₹${c.discount_value} OFF`}
                            </span>
                            {isApplied ? (
                              <span className="chip-applied-badge">✓ Applied</span>
                            ) : (
                              <span className="chip-tap-badge">Tap to Apply</span>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              <div className="coupon-section">
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  value={couponInput}
                  onChange={e =>
                    setCouponInput(
                      e.target.value
                    )
                  }
                  onKeyDown={e => {
                    if (e.key === "Enter") handleApplyCoupon();
                  }}
                />

                <button
                  className="apply-btn"
                  onClick={
                    handleApplyCoupon
                  }
                >
                  Apply
                </button>
              </div>
            </>
          )}

          {/* SUBTOTAL */}

          <div className="summary-row">

            <span className="summary-label">
              Subtotal
            </span>

            <span className="summary-value">
              ₹{subtotal.toLocaleString()}
            </span>

          </div>

          {/* DISCOUNT */}

          {appliedCoupon && (

            <div
              className="summary-row"
              style={{
                color: "#059669"
              }}
            >

              <span
                className="summary-label"
                style={{
                  color: "#059669"
                }}
              >
                Discount (
                {appliedCoupon.code}
                )
              </span>

              <span
                className="summary-value"
                style={{
                  color: "#059669"
                }}
              >
                -₹
                {discountAmount.toLocaleString()}
              </span>

            </div>

          )}

          {/* SHIPPING ROW */}
          <div className="summary-row">
            <span
              className="summary-label"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Truck size={15} color="#5b2c6f" />
              Shipping
            </span>

            {isFreeShipping && shippingInfo.threshold > 0 && subtotal >= shippingInfo.threshold ? (
              <span
                className="summary-value"
                style={{
                  color: "#059669",
                  fontWeight: 700,
                }}
              >
                FREE
              </span>
            ) : (
              <span
                className="summary-value"
                style={{
                  color: "#6b21a8",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                Calculated at checkout
              </span>
            )}
          </div>

          {/* STATE SHIPPING NOTICE */}
          <div
            style={{
              background: "#faf5ff",
              border: "1px solid #e9d5ff",
              color: "#581c87",
              padding: "9px 12px",
              borderRadius: "8px",
              fontSize: "11.5px",
              lineHeight: "1.45",
              margin: "8px 0 10px 0",
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
            }}
          >
            <Truck size={15} color="#7c3aed" style={{ flexShrink: 0, marginTop: "2px" }} />
            <div>
              <span><strong>Note:</strong> Shipping charges may vary based on state and number of pieces, and will be calculated at checkout.</span>
            </div>
          </div>

          {/* FREE SHIPPING PROGRESS (Only when free shipping threshold is configured > 0) */}
          {cartItems.length > 0 && shippingSettings.is_enabled !== false && shippingInfo.threshold > 0 && (
            isFreeShipping ? (
              <div
                style={{
                  fontSize: "11px",
                  color: "#059669",
                  backgroundColor: "#ecfdf5",
                  border: "1px solid #a7f3d0",
                  padding: "7px 10px",
                  borderRadius: "6px",
                  margin: "6px 0 10px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span>✓</span> You have unlocked FREE Shipping on this order!
              </div>
            ) : (
              <div
                style={{
                  fontSize: "11px",
                  color: "#854d0e",
                  backgroundColor: "#fefce8",
                  border: "1px solid #fef08a",
                  padding: "7px 10px",
                  borderRadius: "6px",
                  margin: "6px 0 10px",
                  fontWeight: "500",
                  lineHeight: "1.4",
                }}
              >
                Add <strong>₹{shippingInfo.difference.toLocaleString("en-IN")}</strong> more for <strong>FREE SHIPPING</strong>!
              </div>
            )
          )}

          {/* TOTAL */}
          <div
            className="summary-row total-row"
          >
            <span
              className="summary-label"
              style={{
                fontWeight: 700,
                color: "#5b2c6f"
              }}
            >
              Total
            </span>

            <span className="total-amount">
              ₹
              {finalCartTotal.toLocaleString()}
            </span>
          </div>

          {shippingInfo.threshold > 0 && (
            <p className="shipping-note">
              Standard delivery across India. Free shipping on orders ₹{shippingInfo.threshold.toLocaleString("en-IN")}+
            </p>
          )}

          {/* OUT OF STOCK BANNER */}
          {hasOutOfStockItems && (
            <div
              style={{
                background: "#fef2f2",
                border: "1.5px solid #f87171",
                color: "#b91c1c",
                padding: "10px 12px",
                borderRadius: "10px",
                fontSize: "12px",
                fontWeight: "600",
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <AlertCircle size={18} color="#b91c1c" style={{ flexShrink: 0 }} />
              <span>Some items in your bag are out of stock. Please remove them before proceeding to checkout.</span>
            </div>
          )}

          {/* CHECKOUT */}
          <button
            className="checkout-btn"
            disabled={hasOutOfStockItems}
            style={
              hasOutOfStockItems
                ? {
                    background: "#cbd5e1",
                    color: "#64748b",
                    cursor: "not-allowed",
                    boxShadow: "none",
                  }
                : {}
            }
            onClick={() => {
              if (hasOutOfStockItems) {
                alert("Please remove or adjust out-of-stock items before checkout.");
                return;
              }

              onClose();

              setTimeout(() => {
                navigate(
                  "/checkout",
                  {
                    state: {
                      items: cartItems,
                      appliedCoupon: appliedCoupon,
                      subtotal: subtotal,
                      discountAmount: discountAmount,
                      shippingFee: 0,
                      isFreeShipping: isFreeShipping,
                      shippingThreshold: shippingInfo.threshold,
                      totalAmount: finalCartTotal,
                    }
                  }
                );
              }, 400);
            }}
          >
            {hasOutOfStockItems ? "Resolve Out of Stock Items" : "Proceed to Checkout"}
            <ArrowRight size={16} />
          </button>

          {/* CONTINUE */}

          <button
            className="continue-btn"
            onClick={onClose}
          >
            Continue Shopping
          </button>

        </div>

      </div>

      {/* ========================================================
          COUPON POPUP
      ======================================================== */}

      {couponPopup.show && (

        <div className="coupon-popup-overlay">

          <div className="coupon-popup">

            <div
              className={`coupon-popup-icon ${
                couponPopup.success
                  ? "success"
                  : "error"
              }`}
            >

              {couponPopup.success ? (

                <CheckCircle2 size={32} />

              ) : (

                <AlertCircle size={32} />

              )}

            </div>

            <h2>
              {couponPopup.title}
            </h2>

            <p>
              {couponPopup.message}
            </p>

            <button
              onClick={() =>
                setCouponPopup({
                  show: false,
                  title: "",
                  message: "",
                  success: false,
                })
              }
            >
              OK
            </button>

          </div>

        </div>

      )}

    </>
  );
};

export default CartPage;
